import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import RefreshIcon from '@mui/icons-material/Refresh';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import config from '../config';
import { useAuth } from '../auth/AuthContext';
import {
  ResizableColGroup,
  ResizableHeaderCell,
  TableEmptyRow,
  TableLoadingRow,
  useResizableColumns,
} from '../components/table/ResizableTable';

const TABLE_COLUMNS = [
  { key: 'name', label: 'Account Name', minWidth: 180, maxWidth: 420, defaultWidth: 240 },
  { key: 'api_key', label: 'API Key', minWidth: 120, maxWidth: 220, defaultWidth: 140 },
  { key: 'created_at', label: 'Created', minWidth: 170, maxWidth: 320, defaultWidth: 210 },
  { key: 'updated_at', label: 'Updated', minWidth: 170, maxWidth: 320, defaultWidth: 210 },
];

const initialForm = { name: '', apiKey: '' };

const DynamicMockupsAccountsPage = () => {
  const { fetchWithAuth } = useAuth();
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [form, setForm] = React.useState(initialForm);
  const [formError, setFormError] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const { getWidth, startColumnResize, tableMinWidth } = useResizableColumns(TABLE_COLUMNS, {
    storageKey: 'table.dynamic-mockups-accounts',
  });

  const loadAccounts = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/dynamic-mockups/accounts`);
      if (!resp.ok) {
        throw new Error('Failed to load Dynamic Mockups accounts');
      }
      const payload = await resp.json();
      setItems(Array.isArray(payload.items) ? payload.items : []);
    } catch (err) {
      setError(err.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  React.useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const openDialog = () => {
    setForm(initialForm);
    setFormError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setFormError(null);
    setForm(initialForm);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const name = form.name.trim();
    const apiKey = form.apiKey.trim();
    if (!name || !apiKey) {
      setFormError('Name and API key are required.');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/dynamic-mockups/accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, api_key: apiKey }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.error || 'Failed to create account');
      }
      closeDialog();
      await loadAccounts();
    } catch (err) {
      setFormError(err.message || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Dynamic Mockups Accounts</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadAccounts} disabled={loading}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddBusinessIcon />} onClick={openDialog}>
            Add Account
          </Button>
        </Stack>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: tableMinWidth }} stickyHeader>
            <ResizableColGroup columns={TABLE_COLUMNS} getWidth={getWidth} />
            <TableHead>
              <TableRow>
                {TABLE_COLUMNS.map((column) => (
                  <ResizableHeaderCell
                    key={column.key}
                    column={column}
                    getWidth={getWidth}
                    onResizeStart={startColumnResize}
                  />
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingRow colSpan={TABLE_COLUMNS.length} label="Loading accounts..." />
              ) : items.length === 0 ? (
                <TableEmptyRow colSpan={TABLE_COLUMNS.length} label="No Dynamic Mockups accounts connected yet." />
              ) : (
                items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{item.name || '—'}</TableCell>
                    <TableCell>{item.has_api_key ? 'Configured' : 'Missing'}</TableCell>
                    <TableCell>
                      {item.created_at ? new Date(item.created_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>
                      {item.updated_at ? new Date(item.updated_at).toLocaleString() : '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>Add Dynamic Mockups Account</DialogTitle>
        <DialogContent>
          <Stack component="form" spacing={2} sx={{ mt: 1 }} onSubmit={handleSubmit}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              fullWidth
              autoFocus
            />
            <TextField
              label="API Key"
              value={form.apiKey}
              onChange={(event) => setForm((prev) => ({ ...prev, apiKey: event.target.value }))}
              fullWidth
              multiline
              minRows={2}
              placeholder="keystring:secret"
              InputProps={{
                startAdornment: <VpnKeyIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            {formError ? <Alert severity="error">{formError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving…' : 'Create Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DynamicMockupsAccountsPage;
